import { GraphNodeType } from "@custom-types/graphs"
import { NodeProperties } from "@features/builder/graph/types"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import CategoricalNodeTable from "@features/builder/NodeDrawer/NodeForm/CategoricalNodeTable"
import ContinuousNodeTable from "@features/builder/NodeDrawer/NodeForm/ContinuousNodeTable"
import DiscretePropertiesTable from "@features/builder/NodeDrawer/NodeForm/DiscretePropertiesTable"
import { LabelField } from "@features/builder/NodeDrawer/NodeForm/LabelField"
import NodeExpressionAccordion from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/NodeExpressionAccordion"
import { getDefaultValues } from "@features/builder/NodeDrawer/NodeForm/NodeFormPreview/helpers"
import { linkParse } from "@components/NodeLink/linkHelpers"
import NodePropertiesAccordion from "@features/builder/NodeDrawer/NodeForm/NodePropertiesAccordion"
import TableNodeTable from "@features/builder/NodeDrawer/NodeForm/NodeTableAccordion/TableNodeTable"
import NodeTypeField from "@features/builder/NodeDrawer/NodeForm/NodeTypeField"
import PropertyField from "@features/builder/NodeDrawer/NodeForm/PropertyField"
import { Alert, CircularProgress, Grid } from "@mui/material"
import { useGetUnpackedGraphDataQuery } from "@store/services/graphsService"
import { useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"

type NodeFormPreviewProps = {
  linkedNodeId: string
}

const NodeFormPreview = (props: NodeFormPreviewProps) => {
  const link = linkParse(props.linkedNodeId)
  const skip = !link?.org || !link?.div || !link?.prod || !link?.family || !link?.version
  const graphKey = `${link?.org}#${link?.div}#${link?.prod}#${link?.family}_${link?.version}`
  const { data, isFetching, status } = useGetUnpackedGraphDataQuery(graphKey, { skip })

  const nodeEntries = Object.entries(data?.meta.nodes ?? {})
  const [nodeId, nodeData] = nodeEntries.find(([, node]) => node.label === link?.node) ?? []

  const allNodes = useMemo(
    () => [...(data?.graph.nodes ?? []), ...(data?.graph.attributes ?? [])],
    [data],
  )
  const node = allNodes.find((n) => n.name === nodeId)

  const form = useForm<NodeFormValues>({
    defaultValues: getDefaultValues(node, nodeData),
    disabled: true,
  })

  useEffect(() => {
    form.reset(getDefaultValues(node, nodeData))
  }, [form, node, nodeData])

  const nodeLabelMap = useMemo(() => {
    return nodeEntries.reduce<Record<string, string>>((acc, [id, node]) => {
      acc[id] = node.label
      return acc
    }, {})
  }, [nodeEntries])

  const nodeTypeMap = useMemo(() => {
    return allNodes.reduce<Record<string, GraphNodeType>>((acc, node) => {
      acc[node.name] = node.type
      return acc
    }, {})
  }, [allNodes])

  if (isFetching) {
    return (
      <Grid container item direction="column" alignContent="center" justifyContent="center">
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  if (!data || !node || !nodeData) {
    return (
      <Alert severity="error">
        There was a problem loading linked node data. <br />
        You may not have access to the node's linked graph.
      </Alert>
    )
  }

  const originalNodeUnitFormat = nodeData?.["Unit Format"] ?? ""

  const fields = []
  const nodeTypeField = <NodeTypeField control={form.control} name="type" />

  const linkField = (
    <NodePropertiesAccordion.LinkField
      control={form.control}
      name="link"
      isFormDisabled={form.formState.disabled}
    />
  )

  switch (node.type) {
    case GraphNodeType.TABLE:
      fields.push(<LabelField control={form.control} name="label" />)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion id="node-properties-preview">
          <NodePropertiesAccordion.NodeTagsField nodeId={nodeId ?? ""} />
          <NodePropertiesAccordion.GraphNodeTagsField nodeId={nodeId ?? ""} />
          <Grid item>
            <PropertyField
              control={form.control}
              label="Historic Mnemonic"
              name="historic_mnemonic"
            />
          </Grid>
          <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
          <NodePropertiesAccordion.DescriptionField />
          <NodePropertiesAccordion.CommentsField />
          <Grid item>{linkField}</Grid>
        </NodePropertiesAccordion>,
      )

      fields.push(
        <NodeDrawerAccordion id="table-attributes-preview" label="Table Data">
          <Grid container item gap={1}>
            <TableNodeTable />
          </Grid>
        </NodeDrawerAccordion>,
      )
      break
    case GraphNodeType.CATEGORICAL:
      fields.push(<LabelField control={form.control} name="label" />)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion id="node-properties-preview">
          <NodePropertiesAccordion.NodeTagsField nodeId={nodeId ?? ""} />
          <NodePropertiesAccordion.GraphNodeTagsField nodeId={nodeId ?? ""} />
          <Grid item>
            <PropertyField
              control={form.control}
              label="Historic Mnemonic"
              name="historic_mnemonic"
            />
          </Grid>
          <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
          <NodePropertiesAccordion.DescriptionField />
          <NodePropertiesAccordion.CommentsField />
          <Grid item>{linkField}</Grid>
        </NodePropertiesAccordion>,
      )
      fields.push(
        <NodeDrawerAccordion id="properties-table-preview" label="Properties table">
          <Grid container item gap={1}>
            <CategoricalNodeTable
              nodeLabelMap={nodeLabelMap}
              nodeTypeMap={nodeTypeMap}
              nodeProperties={node.properties as NodeProperties}
            />
          </Grid>
        </NodeDrawerAccordion>,
      )
      break
    case GraphNodeType.CONTINOUS:
      fields.push(<LabelField control={form.control} name="label" />)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion id="node-properties-preview">
          <NodePropertiesAccordion.NodeTagsField nodeId={nodeId ?? ""} />
          <NodePropertiesAccordion.GraphNodeTagsField nodeId={nodeId ?? ""} />
          <Grid item>
            <PropertyField
              control={form.control}
              label="Historic Mnemonic"
              name="historic_mnemonic"
            />
          </Grid>
          <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
          <NodePropertiesAccordion.DescriptionField />
          <NodePropertiesAccordion.CommentsField />
          <Grid item>{linkField}</Grid>
        </NodePropertiesAccordion>,
      )
      fields.push(
        <NodeDrawerAccordion id="properties-table-preview" label="Properties table">
          <Grid item>
            <ContinuousNodeTable
              nodeLabelMap={nodeLabelMap}
              nodeTypeMap={nodeTypeMap}
              type={node.type}
              nodeProperties={node.properties as NodeProperties}
            />
          </Grid>
        </NodeDrawerAccordion>,
      )
      break
    case GraphNodeType.DETERMINISTIC:
      fields.push(<LabelField control={form.control} name="label" />)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion id="node-properties-preview">
          <NodePropertiesAccordion.NodeTagsField nodeId={nodeId ?? ""} />
          <NodePropertiesAccordion.GraphNodeTagsField nodeId={nodeId ?? ""} />
          <Grid item>
            <PropertyField
              control={form.control}
              label="Historic Mnemonic"
              name="historic_mnemonic"
            />
          </Grid>
          <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
          <NodePropertiesAccordion.DescriptionField />
          <NodePropertiesAccordion.CommentsField />
          <Grid item>{linkField}</Grid>
        </NodePropertiesAccordion>,
      )
      fields.push(<NodeExpressionAccordion />)
      break
    case GraphNodeType.DISCRETE:
      fields.push(<LabelField control={form.control} name="label" />)
      fields.push(nodeTypeField)
      fields.push(
        <NodePropertiesAccordion id="node-properties-preview">
          <NodePropertiesAccordion.NodeTagsField nodeId={nodeId ?? ""} />
          <NodePropertiesAccordion.GraphNodeTagsField nodeId={nodeId ?? ""} />
          <Grid item>
            <PropertyField
              control={form.control}
              label="Historic Mnemonic"
              name="historic_mnemonic"
            />
          </Grid>
          <NodePropertiesAccordion.UnitField originalNodeUnitFormat={originalNodeUnitFormat} />
          <NodePropertiesAccordion.DescriptionField />
          <NodePropertiesAccordion.CommentsField />
          <Grid item>{linkField}</Grid>
        </NodePropertiesAccordion>,
      )

      fields.push(
        <NodeDrawerAccordion id="properties-table-preview" label="Properties table">
          <Grid item>
            <DiscretePropertiesTable
              nodeLabelMap={nodeLabelMap}
              nodeTypeMap={nodeTypeMap}
              nodeProperties={node.properties as NodeProperties}
            />
          </Grid>
        </NodeDrawerAccordion>,
      )
      break
  }

  return (
    <FormProvider {...form}>
      <form>
        <Grid container item direction="column" xs gap={1} sx={{ p: 0 }}>
          {fields.map((field, i) => (
            <Grid item key={i} sx={{ p: 0, m: 0, maxWidth: "100% !important" }}>
              {field}
            </Grid>
          ))}
        </Grid>
      </form>
    </FormProvider>
  )
}

export default NodeFormPreview
